/**
 * ChakraTogglesPanel (Panel B) - Floating panel for chakra toggle management
 *
 * Acts as a proxy to original toggles in planet details.
 * Reads initial state from chartStore (independent of tab visibility).
 * Subscribes to ChakraToggleEvent for real-time updates.
 * Emits events to control original toggles.
 *
 * Features:
 * - Popover UI like StartPointSelector
 * - List of enabled chakra toggles
 * - Click to enable/disable via event system
 * - Synced with original toggles
 * - Initializes from chartStore on mount (no dependency on tabs)
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Badge,
    Text,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverBody,
    useDisclosure,
    VStack,
    HStack,
    Switch
} from '@chakra-ui/react';
import { CHAKRA_TOGGLE_EVENT_TYPE, ChakraToggleEvent } from '../../universal_architecture/types/EventTypes';
import { useMultipleCharts } from '../../MultipleChartsContext';
import { globalEventStream } from '../../universal_architecture/core/EventStream';
import { DIVISIONAL_CHARTS } from '../../../../../config/divisionalCharts';

export interface ChakraToggleItem {
    toggleId: string;
    toggleName: string;
    enabled: boolean;
}

export interface ChakraTogglesPanelProps {
    chartId: string;
    buttonStyle?: React.CSSProperties;
}

/**
 * Configuration for all chakra toggles that Panel B should track
 * Maps chakraType to toggleId and toggleName
 */
const CHAKRA_TOGGLE_CONFIG: Array<{
    chakraType: string;
    settingKey: string;
    toggleId: string;
    toggleName: string;
}> = [
    // Muhurta and Nakshatra (from divisionalChakraConfig)
    { chakraType: 'muhurta', settingKey: 'showMUHURTAChakra', toggleId: 'muhurta-chakra-toggle', toggleName: 'Muhurta Chakra' },
    { chakraType: 'nakshatra', settingKey: 'showNAKSHATRAChakra', toggleId: 'nakshatra-chakra-toggle', toggleName: 'Nakshatra Chakra' },
    // D7-D60 from DIVISIONAL_CHARTS
    ...DIVISIONAL_CHARTS
        .filter(chart => chart.toggleConfig)
        .map(chart => ({
            chakraType: chart.id.split('-')[0], // 'd7', 'd8', etc.
            settingKey: `show${chart.id.split('-')[0].toUpperCase()}Chakra`,
            toggleId: `${chart.id}-toggle`,
            toggleName: chart.toggleConfig!.label
        }))
];

/**
 * ChakraTogglesPanel - Panel B for managing chakra toggles
 */
export const ChakraTogglesPanel: React.FC<ChakraTogglesPanelProps> = ({ chartId, buttonStyle }) => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [toggles, setToggles] = useState<Map<string, ChakraToggleItem>>(new Map());
    const { getActiveChart, updateChart, activeChartId } = useMultipleCharts();
    const initializedForChartRef = useRef<string | null>(null);

    /**
     * Initialize toggles from chartStore on mount (once per chartId)
     * This ensures Panel B shows correct state even when tabs are collapsed
     */
    useEffect(() => {
        // Only initialize once per chartId
        if (initializedForChartRef.current === chartId) return;

        const activeChart = getActiveChart();
        if (!activeChart?.settings) return;

        const initialToggles = new Map<string, ChakraToggleItem>();

        CHAKRA_TOGGLE_CONFIG.forEach(config => {
            const isEnabled = (activeChart.settings as any)[config.settingKey] === true;
            if (isEnabled) {
                initialToggles.set(config.toggleId, {
                    toggleId: config.toggleId,
                    toggleName: config.toggleName,
                    enabled: true
                });
            }
        });

        if (initialToggles.size > 0) {
            setToggles(initialToggles);
        }

        initializedForChartRef.current = chartId;
    }, [chartId, getActiveChart]);

    /**
     * Emit event to control original toggle
     */
    const emitToggleCommand = useCallback((toggleId: string, toggleName: string, enabled: boolean) => {
        const event: ChakraToggleEvent = {
            id: `panel-b-command-${toggleId}-${Date.now()}`,
            type: CHAKRA_TOGGLE_EVENT_TYPE,
            source: 'panel-b',
            timestamp: Date.now(),
            from: 'panel-b',
            to: toggleId, // Direct message to specific toggle
            data: {
                toggleId,
                toggleName,
                enabled,
                chartId
            }
        };

        globalEventStream.emit(event);
    }, [chartId]);

    /**
     * Handle toggle click in Panel B
     * Updates chartStore directly and emits events for renderers
     */
    const handleToggleClick = useCallback(async (item: ChakraToggleItem) => {
        const newEnabled = !item.enabled;

        // Update local state
        setToggles(prev => {
            const updated = new Map(prev);
            updated.set(item.toggleId, { ...item, enabled: newEnabled });
            return updated;
        });

        // Find config for this toggle to get settingKey and chakraType
        const config = CHAKRA_TOGGLE_CONFIG.find(c => c.toggleId === item.toggleId);
        if (config && activeChartId && updateChart) {
            // Update chartStore directly (single source of truth)
            await updateChart(activeChartId, {
                settings: {
                    [config.settingKey]: newEnabled
                }
            });

            // Emit event for chakra renderers (e.g., 'd7.chakra.toggle', 'd8.chakra.toggle')
            globalEventStream.emit({
                id: `${config.chakraType}-toggle-panel-b-${activeChartId}-${Date.now()}`,
                type: `${config.chakraType}.chakra.toggle`,
                source: 'panel-b',
                timestamp: Date.now(),
                chartId: activeChartId,
                data: {
                    chartId: activeChartId,
                    [config.settingKey]: newEnabled
                }
            });
        }

        // Emit event for UniversalDivisionalToggle sync
        emitToggleCommand(item.toggleId, item.toggleName, newEnabled);
    }, [emitToggleCommand, activeChartId, updateChart]);

    /**
     * Subscribe to chakra toggle events
     */
    useEffect(() => {
        const subscription = globalEventStream.subscribe(
            CHAKRA_TOGGLE_EVENT_TYPE,
            (event: ChakraToggleEvent) => {
                // Only process events for this chart
                if (event.data.chartId !== chartId) return;
                // Ignore self-emitted events
                if (event.from === 'panel-b') return;

                const { toggleId, toggleName, enabled } = event.data;

                setToggles(prev => {
                    const updated = new Map(prev);

                    if (enabled) {
                        // Add or update toggle in list
                        updated.set(toggleId, { toggleId, toggleName, enabled });
                    } else {
                        // Keep disabled toggle in list (until page reload)
                        // but mark as disabled
                        if (updated.has(toggleId)) {
                            updated.set(toggleId, { toggleId, toggleName, enabled: false });
                        }
                    }

                    return updated;
                });
            }
        );

        return () => subscription?.unsubscribe?.();
    }, [chartId]);

    // Convert Map to sorted array for rendering
    const toggleList = Array.from(toggles.values()).sort((a, b) =>
        a.toggleName.localeCompare(b.toggleName)
    );

    // Count enabled toggles for badge
    const enabledCount = toggleList.filter(t => t.enabled).length;

    return (
        <Popover isOpen={isOpen} onOpen={onOpen} onClose={onClose} placement="bottom-start">
            <PopoverTrigger>
                <Box
                    as="button"
                    height={buttonStyle?.height || '24px'}
                    px={buttonStyle?.padding?.toString().split(' ')[1] || '4px'}
                    fontSize={buttonStyle?.fontSize || '11px'}
                    lineHeight={buttonStyle?.lineHeight || '16px'}
                    border="1px solid transparent"
                    borderRadius={buttonStyle?.borderRadius || '6px'}
                    bg="transparent"
                    color="#718096"
                    cursor="pointer"
                    minWidth={buttonStyle?.minWidth || '24px'}
                    display="flex"
                    alignItems="center"
                    gap="4px"
                    _hover={{ bg: '#EDF2F7' }}
                    transition="background 0.15s ease"
                >
                    <span>toggle</span>
                    {enabledCount > 0 && (
                        <Badge
                            bg="#E2E8F0"
                            color="#4A5568"
                            fontSize="9px"
                            px="5px"
                            py="1px"
                            borderRadius="full"
                            minW="16px"
                            textAlign="center"
                            fontWeight="500"
                        >
                            {enabledCount}
                        </Badge>
                    )}
                </Box>
            </PopoverTrigger>

            <PopoverContent width="auto" minW="180px" maxW="280px" _focus={{ boxShadow: 'sm' }}>
                <PopoverBody p={2}>
                    {toggleList.length === 0 ? (
                        <Text fontSize="xs" color="gray.500" textAlign="center" py={2}>
                            No active chakras
                        </Text>
                    ) : (
                        <VStack spacing={1} align="stretch">
                            {toggleList.map(item => (
                                <HStack
                                    key={item.toggleId}
                                    justify="space-between"
                                    p={1}
                                    borderRadius="sm"
                                    bg={item.enabled ? 'green.50' : 'gray.50'}
                                    _hover={{ bg: item.enabled ? 'green.100' : 'gray.100' }}
                                    cursor="pointer"
                                    onClick={() => handleToggleClick(item)}
                                >
                                    <Text
                                        fontSize="xs"
                                        color={item.enabled ? 'gray.700' : 'gray.400'}
                                        fontWeight={item.enabled ? 'medium' : 'normal'}
                                        noOfLines={1}
                                    >
                                        {item.toggleName}
                                    </Text>
                                    <Switch
                                        size="sm"
                                        isChecked={item.enabled}
                                        onChange={() => handleToggleClick(item)}
                                        colorScheme="green"
                                        pointerEvents="none"
                                    />
                                </HStack>
                            ))}
                        </VStack>
                    )}
                </PopoverBody>
            </PopoverContent>
        </Popover>
    );
};
